import React, { useMemo, useEffect } from "react";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useSortBy,
  TableOptions,
  useAsyncDebounce
} from "react-table";
import { ColumnFilter } from "./ColumnFilter";


interface TableState {
  globalFilter: string;
  pageIndex: number;
  pageSize: number;
}

export function DataGrid({
  columns,
  data,
  onRowHandler,  
  filter
}: any) {

  const defaultColumn: any = useMemo(
    () => ({
      Filter: ({ column }: any) => <ColumnFilter column={column} />,
    }),
    []
  );

  

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter
  } = useTable<TableState>(
    {
      columns,
      data,
      defaultColumn,
      manualPagination: true
    } as TableOptions<any>,
    useFilters,
    useGlobalFilter,
    useSortBy
  ) as any;

  // Use useAsyncDebounce to debounce the filter changes
  const onFilterChange = useAsyncDebounce(value => {
    setGlobalFilter(value || "");
  }, 200);

  // Update the filter when the filter prop changes
  useEffect(() => {
    onFilterChange(filter);
  }, [filter, onFilterChange]);

  return (
    <div className="w-full overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
          <table {...getTableProps()} className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              {headerGroups?.map((headerGroup: any, index: number) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index} className="border-b border-gray-200">
                  {headerGroup.headers?.map((column: any) => {
                    const { key, ...rest } = column.getHeaderProps(
                      column.getSortByToggleProps()
                    );
                    return (
                      <th
                        key={key}
                        scope="col"
                        className="group px-6 py-4 text-center text-sm font-semibold text-gray-900"
                        {...rest}
                        style={{ minWidth: column.minWidth, width: column.width, maxWidth: column.maxWidth }}
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <div className="truncate">{column.render("Header")}</div>
                          <div className="flex-shrink-0">
                            {column.isSorted
                              ? column.isSortedDesc
                                ? <span className="text-gray-600">↓</span>
                                : <span className="text-gray-600">↑</span>
                              : ""}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody
              {...getTableBodyProps()}
              className="divide-y divide-gray-200 bg-white"
            >
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-8 text-sm text-gray-500 text-center">
                    No data available
                  </td>
                </tr>
              ) : (
                rows?.map((row: any) => {
                  prepareRow(row);
                  return (
                    <tr
                      className={`${onRowHandler ? "cursor-pointer hover:bg-gray-50" : ""} transition-colors duration-200`}
                      {...(onRowHandler ? { onClick: () => onRowHandler(row.original) } : {})}
                      {...row.getRowProps()}
                      key={row.id}
                    >
                      {row.cells?.map((cell: any) => {
                        const { key, ...rest } = cell.getCellProps();
                        return (
                          <td
                            key={key}
                            {...rest}
                            className="whitespace-nowrap px-6 py-2.5 text-sm text-gray-700 text-center"
                            role="cell"
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
